import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import schoolLogo from "../assets/rsps-logo2.png";

const SCHOOL_NAME = "Rising Stars Public School";
const SCHOOL_ADDRESS = "Rising Stars Public School, India";

function toNumber(value) {
  const parsed = Number(
    String(value ?? "")
      .replace(/,/g, "")
      .trim(),
  );
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatNumber(value) {
  return toNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function hasNumericValue(value) {
  if (value === null || value === undefined || value === "") return false;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed);
}

function formatCurrency(value) {
  if (!hasNumericValue(value)) return String(value || "-").trim() || "-";
  return `INR ${formatNumber(value)}`;
}

function normaliseDate(value) {
  if (!value) return new Date().toISOString().slice(0, 10);
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [day, month, year] = value.split("/");
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
  }
  if (typeof value === "object" && value?.seconds) {
    return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  }
  return new Date().toISOString().slice(0, 10);
}

function formatDate(value) {
  const normalised = normaliseDate(value);
  const parsed = new Date(`${normalised}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return normalised;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function twoDigitWords(value) {
  const singles = [
    "Zero",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
  ];
  const teens = [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  if (value < 10) return singles[value];
  if (value < 20) return teens[value - 10];
  const ten = Math.floor(value / 10);
  const unit = value % 10;
  return `${tens[ten]}${unit ? ` ${singles[unit]}` : ""}`.trim();
}

function threeDigitWords(value) {
  const hundred = Math.floor(value / 100);
  const remainder = value % 100;
  const parts = [];
  if (hundred) parts.push(`${twoDigitWords(hundred)} Hundred`);
  if (remainder) parts.push(twoDigitWords(remainder));
  return parts.join(" ").trim();
}

function numberToWords(value) {
  const amount = Math.floor(toNumber(value));
  if (amount === 0) return "Zero Only";

  const crore = Math.floor(amount / 10000000);
  const lakh = Math.floor((amount % 10000000) / 100000);
  const thousand = Math.floor((amount % 100000) / 1000);
  const hundred = amount % 1000;
  const parts = [];

  if (crore) parts.push(`${threeDigitWords(crore)} Crore`);
  if (lakh) parts.push(`${threeDigitWords(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigitWords(thousand)} Thousand`);
  if (hundred) parts.push(threeDigitWords(hundred));

  return `${parts.join(" ").replace(/\s+/g, " ").trim()} Only`.trim();
}

function normaliseFeeItems(items) {
  return Array.isArray(items)
    ? items
        .map((item, index) => ({
          slNo: item?.slNo || index + 1,
          description: String(item?.description || item?.label || "").trim(),
          due: toNumber(item?.due),
          concession: toNumber(item?.concession),
          paid: toNumber(item?.paid),
        }))
        .filter((item) => item.description)
    : [];
}

function normalisePaymentHistory(items) {
  return Array.isArray(items)
    ? items
        .map((item, index) => {
          const paymentDate =
            item?.paymentDate || item?.date || item?.recordedAt;
          return {
            index: index + 1,
            paymentDate: paymentDate || new Date().toISOString(),
            paymentMode: String(item?.paymentMode || "-").trim(),
            transactionId: String(item?.transactionId || "-").trim(),
            feePaymentAmount: toNumber(item?.feePaymentAmount),
            transportPaymentAmount: toNumber(item?.transportPaymentAmount),
            arrearPaymentAmount: toNumber(item?.arrearPaymentAmount),
            miscPaymentAmount: toNumber(item?.miscPaymentAmount),
            miscPaymentRemark: String(item?.miscPaymentRemark || "").trim(),
            remarks: String(item?.remarks || "").trim(),
            recordedAt: item?.recordedAt || null,
          };
        })
        .filter(
          (item) =>
            item.feePaymentAmount > 0 ||
            item.transportPaymentAmount > 0 ||
            item.arrearPaymentAmount > 0 ||
            item.miscPaymentAmount > 0 ||
            item.transactionId !== "-",
        )
    : [];
}

function getPaymentHistoryTotal(item) {
  return (
    toNumber(item?.feePaymentAmount) +
    toNumber(item?.transportPaymentAmount) +
    toNumber(item?.arrearPaymentAmount) +
    toNumber(item?.miscPaymentAmount)
  );
}

function getMostRecentPaymentHistoryEntry(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return [...items].sort((a, b) => {
    const aTime = new Date(a.recordedAt || a.paymentDate || 0).getTime();
    const bTime = new Date(b.recordedAt || b.paymentDate || 0).getTime();
    return bTime - aTime;
  })[0];
}

function buildRecentTransactionLabel(item) {
  if (!item) return "-";
  return [
    String(item.transactionId || "-").trim(),
    formatDate(item.paymentDate),
    formatCurrency(getPaymentHistoryTotal(item)),
  ]
    .filter(Boolean)
    .join(" | ");
}

function loadImageAsDataUrl(src, options = {}) {
  const { opacity = 1 } = options;
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Unable to draw receipt logo."));
        return;
      }
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.globalAlpha = Math.max(0, Math.min(1, opacity));
      context.drawImage(image, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => reject(new Error("Unable to load receipt logo."));
    image.src = src;
  });
}

export function buildReceiptTotalInWords(totalAmount) {
  return numberToWords(totalAmount);
}

export function buildReceiptPayload(receipt = {}) {
  const incomingFeeItems = normaliseFeeItems(receipt.feeItems);
  const paymentHistory = normalisePaymentHistory(receipt.paymentHistory);
  const currentTransaction = receipt.currentTransaction
    ? normalisePaymentHistory([receipt.currentTransaction])[0]
    : null;

  if (currentTransaction) {
    const currentSignature = JSON.stringify([
      currentTransaction.transactionId,
      normaliseDate(currentTransaction.paymentDate),
      currentTransaction.feePaymentAmount,
      currentTransaction.transportPaymentAmount,
      currentTransaction.arrearPaymentAmount,
      currentTransaction.miscPaymentAmount,
    ]);
    const alreadyPresent = paymentHistory.some((entry) => {
      const entrySignature = JSON.stringify([
        entry.transactionId,
        normaliseDate(entry.paymentDate),
        entry.feePaymentAmount,
        entry.transportPaymentAmount,
        entry.arrearPaymentAmount,
        entry.miscPaymentAmount,
      ]);
      return entrySignature === currentSignature;
    });
    if (!alreadyPresent) {
      paymentHistory.unshift(currentTransaction);
    }
  }

  // Build expanded fee items from individual fee structure fields
  const expandedFeeItems = [];
  let slNo = 1;

  // Monthly fees breakdown
  if (toNumber(receipt.monthlyTuitionFee) > 0) {
    const months = toNumber(receipt.monthlyFeeMonths) || 12;
    expandedFeeItems.push({
      slNo: slNo++,
      description: `Tuition Fee (${months} months)`,
      due: toNumber(receipt.monthlyTuitionFee) * months,
      concession: 0,
      paid: toNumber(receipt.monthlyTuitionFee) * months,
    });
  }
  if (toNumber(receipt.monthlyMaintenanceFee) > 0) {
    const months = toNumber(receipt.monthlyFeeMonths) || 12;
    expandedFeeItems.push({
      slNo: slNo++,
      description: `Maintenance Fee (${months} months)`,
      due: toNumber(receipt.monthlyMaintenanceFee) * months,
      concession: 0,
      paid: toNumber(receipt.monthlyMaintenanceFee) * months,
    });
  }
  if (toNumber(receipt.monthlySportsFee) > 0) {
    const months = toNumber(receipt.monthlyFeeMonths) || 12;
    expandedFeeItems.push({
      slNo: slNo++,
      description: `Sports Fee (${months} months)`,
      due: toNumber(receipt.monthlySportsFee) * months,
      concession: 0,
      paid: toNumber(receipt.monthlySportsFee) * months,
    });
  }
  if (toNumber(receipt.monthlyComputerFee) > 0) {
    const months = toNumber(receipt.monthlyFeeMonths) || 12;
    expandedFeeItems.push({
      slNo: slNo++,
      description: `Computer Fee (${months} months)`,
      due: toNumber(receipt.monthlyComputerFee) * months,
      concession: 0,
      paid: toNumber(receipt.monthlyComputerFee) * months,
    });
  }

  // Annual charges breakdown
  if (toNumber(receipt.otherAnnualCharge) > 0) {
    expandedFeeItems.push({
      slNo: slNo++,
      description: "Annual Charge",
      due: toNumber(receipt.otherAnnualCharge),
      concession: 0,
      paid: toNumber(receipt.otherAnnualCharge),
    });
  }
  if (toNumber(receipt.otherIdCharge) > 0) {
    expandedFeeItems.push({
      slNo: slNo++,
      description: "ID Charge",
      due: toNumber(receipt.otherIdCharge),
      concession: 0,
      paid: toNumber(receipt.otherIdCharge),
    });
  }
  if (toNumber(receipt.otherDiaryCharge) > 0) {
    expandedFeeItems.push({
      slNo: slNo++,
      description: "Diary Charge",
      due: toNumber(receipt.otherDiaryCharge),
      concession: 0,
      paid: toNumber(receipt.otherDiaryCharge),
    });
  }
  if (toNumber(receipt.otherExamFee) > 0) {
    expandedFeeItems.push({
      slNo: slNo++,
      description: "Exam Fee",
      due: toNumber(receipt.otherExamFee),
      concession: 0,
      paid: toNumber(receipt.otherExamFee),
    });
  }

  // Custom charges
  if (Array.isArray(receipt.customCharges)) {
    receipt.customCharges.forEach((charge) => {
      if (charge && charge.name && toNumber(charge.amount) > 0) {
        expandedFeeItems.push({
          slNo: slNo++,
          description: String(charge.name).trim(),
          due: toNumber(charge.amount),
          concession: 0,
          paid: toNumber(charge.amount),
        });
      }
    });
  }

  // Transport/Bus fee
  if (toNumber(receipt.transportFeePerMonth) > 0) {
    const months = toNumber(receipt.transportMonths) || 11;
    expandedFeeItems.push({
      slNo: slNo++,
      description: `Bus Fee (${months} months)`,
      due: toNumber(receipt.transportFeePerMonth) * months,
      concession: 0,
      paid: toNumber(receipt.transportFeePerMonth) * months,
    });
  }

  // Use expanded items if available, otherwise fall back to incoming items
  const feeItems =
    expandedFeeItems.length > 0 ? expandedFeeItems : incomingFeeItems;

  const totalAmount =
    receipt.totalAmount !== undefined
      ? toNumber(receipt.totalAmount)
      : feeItems.reduce((sum, item) => sum + item.paid, 0);

  const totalPaidAmount = paymentHistory.reduce(
    (sum, item) => sum + getPaymentHistoryTotal(item),
    0,
  );

  const incomingPaymentDetails =
    receipt.paymentDetails && typeof receipt.paymentDetails === "object"
      ? receipt.paymentDetails
      : {};

  return {
    schoolName: String(receipt.schoolName || SCHOOL_NAME).trim(),
    schoolAddress: String(receipt.schoolAddress || SCHOOL_ADDRESS).trim(),
    logo: receipt.logo || schoolLogo,
    receiptNo: String(receipt.receiptNo || receipt.transactionNo || "-").trim(),
    date: formatDate(receipt.date),
    admissionNo: String(
      receipt.admissionNo || receipt.admissionNumber || receipt.uid || "-",
    ).trim(),
    studentName: String(
      receipt.studentName || receipt.name || receipt.childName || "-",
    ).trim(),
    session: String(receipt.session || receipt.academicYear || "-").trim(),
    className: String(
      receipt.className || receipt.class || receipt.classLabel || "-",
    ).trim(),
    installment: String(receipt.installment || "-").trim(),
    counterNo: String(receipt.counterNo || "DPS-RECEIPT").trim(),
    feeItems,
    paymentMode: String(receipt.paymentMode || "-").trim(),
    bank: String(receipt.bank || "-").trim(),
    transactionNo: String(
      receipt.transactionNo || receipt.chequeNo || "-",
    ).trim(),
    paymentDate: formatDate(receipt.paymentDate || receipt.date),
    totalAmount,
    totalInWords: String(
      receipt.totalInWords || numberToWords(totalAmount),
    ).trim(),
    totalPaidAmount,
    totalPaidAmountInWords: String(
      receipt.totalPaidAmountInWords || numberToWords(totalPaidAmount),
    ).trim(),
    qrValue:
      receipt.qrValue ||
      `${receipt.receiptNo || receipt.transactionNo || "receipt"}|${receipt.studentName || receipt.admissionNo || "student"}|${formatNumber(totalAmount)}`,
    paymentHistory,
    paymentDetails: {
      student:
        incomingPaymentDetails.student ||
        receipt.studentName ||
        receipt.name ||
        "-",
      uid:
        incomingPaymentDetails.uid || receipt.admissionNo || receipt.uid || "-",
      className:
        incomingPaymentDetails.className ||
        receipt.className ||
        receipt.class ||
        "-",
      monthlyFees:
        incomingPaymentDetails.monthlyFees ?? receipt.monthlyAnnual ?? 0,
      otherCharges:
        incomingPaymentDetails.otherCharges ?? receipt.otherTotal ?? 0,
      transportFees:
        incomingPaymentDetails.transportFees ?? receipt.transportAnnual ?? 0,
      totalOfAllFees:
        incomingPaymentDetails.totalOfAllFees ?? receipt.totalOfAllFees ?? 0,
      previousYearArrears:
        incomingPaymentDetails.previousYearArrears ?? receipt.prevArrear ?? 0,
      arrearPayment:
        incomingPaymentDetails.arrearPayment ?? receipt.arrearPaid ?? 0,
      remainingArrear:
        incomingPaymentDetails.remainingArrear ?? receipt.balanceArrear ?? 0,
      paymentAmount:
        incomingPaymentDetails.paymentAmount ?? receipt.feePaid ?? 0,
      transportAmount:
        incomingPaymentDetails.transportAmount ?? receipt.transportPaid ?? 0,
      paymentDate:
        incomingPaymentDetails.paymentDate ||
        receipt.paymentDate ||
        receipt.date ||
        "-",
      remainingFee:
        incomingPaymentDetails.remainingFee ?? receipt.balanceFee ?? 0,
      remainingTransportFees:
        incomingPaymentDetails.remainingTransportFees ??
        receipt.balanceTransportFees ??
        0,
      status:
        incomingPaymentDetails.status ||
        receipt.status ||
        (toNumber(receipt.totalOutstanding) <= 0 ? "Paid" : "Pending"),
      annualFees:
        incomingPaymentDetails.annualFees ??
        incomingPaymentDetails.monthlyFees ??
        receipt.monthlyAnnual ??
        0,
      customCharges:
        incomingPaymentDetails.customCharges ?? receipt.customChargesTotal ?? 0,
      balanceFee: incomingPaymentDetails.balanceFee ?? receipt.balanceFee ?? 0,
      balanceTransport:
        incomingPaymentDetails.balanceTransport ??
        receipt.balanceTransportFees ??
        0,
      balanceArrear:
        incomingPaymentDetails.balanceArrear ?? receipt.balanceArrear ?? 0,
    },
    footerNote:
      receipt.footerNote ||
      "This is a computer-generated receipt. Signature required.",
    parentCopyLabel: receipt.parentCopyLabel || "PARENT COPY",
  };
}

export async function buildReceiptPdf(receipt = {}) {
  const data = buildReceiptPayload(receipt);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const right = pageWidth - 10;

  const [logoDataUrl, watermarkLogoDataUrl, qrDataUrl] = await Promise.all([
    loadImageAsDataUrl(data.logo),
    loadImageAsDataUrl(data.logo, { opacity: 0.08 }),
    QRCode.toDataURL(String(data.qrValue), {
      margin: 0,
      width: 220,
      errorCorrectionLevel: "M",
      color: { dark: "#000000", light: "#FFFFFF" },
    }),
  ]);

  doc.setDrawColor(160);
  doc.setLineWidth(0.3);
  doc.rect(8, 8, pageWidth - 16, pageHeight - 16);

  // Soft center watermark for brand identity without affecting readability.
  doc.addImage(
    watermarkLogoDataUrl,
    "PNG",
    pageWidth / 2 - 35,
    pageHeight / 2 - 35,
    70,
    70,
  );

  doc.addImage(logoDataUrl, "PNG", 12, 11, 18, 18);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(data.schoolName, pageWidth / 2, 16, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text(data.schoolAddress, pageWidth / 2, 22, { align: "center" });

  doc.setFillColor(228, 228, 228);
  doc.rect(8, 31, pageWidth - 16, 7, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("FEE RECEIPT", pageWidth / 2, 35.9, { align: "center" });

  const infoTop = 43;
  const infoLeft = 12;
  const infoRight = 126;
  const labelWidth = 25;
  const rowGap = 6.2;
  const rows = [
    [
      ["Receipt No", data.receiptNo],
      ["Date", data.date],
    ],
    [
      ["Adm No", data.admissionNo],
      ["Session", data.session],
    ],
    [
      ["Name", data.studentName],
      ["Class", data.className],
    ],
    [
      ["Installment", data.installment],
      ["Counter No", data.counterNo],
    ],
  ];

  rows.forEach((row, index) => {
    const y = infoTop + index * rowGap;
    row.forEach(([label, value], columnIndex) => {
      const x = columnIndex === 0 ? infoLeft : infoRight;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text(`${label} :`, x, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(value || "-"), x + labelWidth, y);
    });
  });

  const tableStart = infoTop + rows.length * rowGap + 2;
  const feeRows = data.feeItems.length
    ? data.feeItems
    : [{ slNo: 1, description: "Fee", due: 0, concession: 0, paid: 0 }];

  autoTable(doc, {
    startY: tableStart,
    head: [["Sl.No", "Description", "Due", "Concession", "Paid"]],
    body: feeRows.map((item) => [
      String(item.slNo),
      item.description,
      formatNumber(item.due),
      formatNumber(item.concession),
      formatNumber(item.paid),
    ]),
    theme: "grid",
    margin: { left: 10, right: 10 },
    styles: {
      font: "helvetica",
      fontSize: 7.8,
      cellPadding: 1.5,
      valign: "middle",
      lineColor: [150, 150, 150],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [230, 230, 230],
      textColor: 20,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 83 },
      2: { cellWidth: 24, halign: "right" },
      3: { cellWidth: 24, halign: "right" },
      4: { cellWidth: 24, halign: "right" },
    },
  });

  const paymentStart = (doc.lastAutoTable?.finalY || tableStart) + 4;
  doc.setFillColor(228, 228, 228);
  doc.rect(8, paymentStart, pageWidth - 16, 7, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("PAY MODE INFORMATION", pageWidth / 2, paymentStart + 4.8, {
    align: "center",
  });

  const sectionTop = paymentStart + 10;
  const sectionLeft = 12;
  const sectionRight = 126;
  const labelColWidth = 28;
  const sectionRowGap = 6.4;

  const paymentRows = [
    [
      ["Pay Mode", data.paymentMode],
      ["Date", data.paymentDate],
    ],
    [
      ["Bank", data.bank],
      ["Number", data.transactionNo],
    ],
  ];

  paymentRows.forEach((row, index) => {
    const y = sectionTop + index * sectionRowGap;
    row.forEach(([label, value], columnIndex) => {
      const x = columnIndex === 0 ? sectionLeft : sectionRight;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text(`${label} :`, x, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(value || "-"), x + labelColWidth, y);
    });
  });

  const totalRowY = sectionTop + paymentRows.length * sectionRowGap + 1.5;
  doc.setFillColor(204, 204, 204);
  doc.rect(8, totalRowY - 4.8, pageWidth - 16, 6, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.text("Total", sectionLeft, totalRowY);
  doc.text(formatNumber(data.totalAmount), right - 2, totalRowY, {
    align: "right",
  });

  const recentTransaction = getMostRecentPaymentHistoryEntry(
    data.paymentHistory,
  );
  const recentTransactionText = buildRecentTransactionLabel(recentTransaction);

  const summaryStartY = totalRowY + 5;
  autoTable(doc, {
    startY: summaryStartY,
    head: [["Summary", "Value"]],
    body: [
      ["Recent Transaction", recentTransactionText],
      ["Total Paid Amount", formatCurrency(data.totalPaidAmount)],
      ["Total Paid Amount in Words", data.totalPaidAmountInWords],
    ],
    theme: "grid",
    margin: { left: 10, right: 10 },
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 1.4,
      valign: "middle",
      lineColor: [180, 180, 180],
      lineWidth: 0.2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [235, 235, 235],
      textColor: 20,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 52, fontStyle: "bold" },
      1: { cellWidth: 136 },
    },
  });

  const paidWordsBottomY = doc.lastAutoTable?.finalY || summaryStartY;

  const details = data.paymentDetails || {};
  const orderedDetailPairs = [
    ["Student", details.student || data.studentName],
    ["UID", details.uid || data.admissionNo],
    ["Class", details.className || data.className],
    ["Monthly Fees", formatCurrency(details.monthlyFees)],
    ["Other Charges", formatCurrency(details.otherCharges)],
    ["Transport Fees", formatCurrency(details.transportFees)],
    ["Total of All Fees", formatCurrency(details.totalOfAllFees)],
    ["Previous Year Arrears", formatCurrency(details.previousYearArrears)],
    ["Arrear Payment", formatCurrency(details.arrearPayment)],
    ["Remaining Arrear", formatCurrency(details.remainingArrear)],
    ["Payment Amount", formatCurrency(details.paymentAmount)],
    ["Transport Amount", formatCurrency(details.transportAmount)],
    ["Payment Date", formatDate(details.paymentDate || data.paymentDate)],
    ["Remaining Fee", formatCurrency(details.remainingFee)],
    ["Remaining Transport", formatCurrency(details.remainingTransportFees)],
    ["Status", String(details.status || "-")],
  ];

  const detailsBody = [];
  for (let index = 0; index < orderedDetailPairs.length; index += 2) {
    const left = orderedDetailPairs[index];
    const rightPair = orderedDetailPairs[index + 1];
    detailsBody.push([
      left[0],
      String(left[1] || "-"),
      rightPair ? rightPair[0] : "",
      rightPair ? String(rightPair[1] || "-") : "",
    ]);
  }

  const detailsStartY = paidWordsBottomY + 5;
  autoTable(doc, {
    startY: detailsStartY,
    head: [["Field", "Value", "Field", "Value"]],
    body: detailsBody,
    theme: "grid",
    margin: { left: 10, right: 10 },
    styles: {
      font: "helvetica",
      fontSize: 7,
      cellPadding: 1.4,
      lineColor: [180, 180, 180],
      lineWidth: 0.2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [235, 235, 235],
      textColor: 20,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 30, fontStyle: "bold" },
      1: { cellWidth: 57 },
      2: { cellWidth: 30, fontStyle: "bold" },
      3: { cellWidth: 57 },
    },
  });

  const historyStartY = (doc.lastAutoTable?.finalY || detailsStartY) + 5;
  const historyRows = Array.isArray(data.paymentHistory)
    ? data.paymentHistory
    : [];
  const historyBody = historyRows.length
    ? historyRows.map((item) => [
        String(item.index || "-"),
        formatDate(item.paymentDate),
        formatCurrency(item.feePaymentAmount),
        formatCurrency(item.transportPaymentAmount),
        formatCurrency(item.arrearPaymentAmount),
        formatCurrency(item.miscPaymentAmount),
        formatCurrency(getPaymentHistoryTotal(item)),
        String(item.paymentMode || "-"),
        String(item.transactionId || "-"),
      ])
    : [["-", "-", "-", "-", "-", "-", "-", "-", "-"]];

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Previous Transactions", sectionLeft, historyStartY - 2);

  autoTable(doc, {
    startY: historyStartY,
    head: [
      [
        "No",
        "Date",
        "Fee",
        "Transport",
        "Arrear",
        "Misc",
        "Total",
        "Mode",
        "Txn ID",
      ],
    ],
    body: historyBody,
    theme: "grid",
    margin: { left: 10, right: 10 },
    styles: {
      font: "helvetica",
      fontSize: 6.4,
      cellPadding: 1.2,
      valign: "middle",
      lineColor: [180, 180, 180],
      lineWidth: 0.2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [235, 235, 235],
      textColor: 20,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 18 },
      2: { cellWidth: 18, halign: "right" },
      3: { cellWidth: 18, halign: "right" },
      4: { cellWidth: 18, halign: "right" },
      5: { cellWidth: 18, halign: "right" },
      6: { cellWidth: 18, halign: "right" },
      7: { cellWidth: 20 },
      8: { cellWidth: 24 },
    },
  });

  let contentBottomY = doc.lastAutoTable?.finalY || historyStartY;
  const minimumFooterTop = Math.max(contentBottomY + 8, pageHeight - 42);

  if (minimumFooterTop > pageHeight - 18) {
    doc.addPage();
    contentBottomY = 14;
  }

  const footerTopY = Math.max(contentBottomY + 8, 18);
  const qrY = footerTopY;
  doc.addImage(qrDataUrl, "PNG", 10, qrY, 22, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.2);
  doc.text("Note : 356", 34, qrY + 6);

  const footerMaxWidth = pageWidth - 70;
  const footerLines = doc.splitTextToSize(data.footerNote, footerMaxWidth);
  const footerFontSize = 7;
  const footerLineHeight = 3.6;
  const footerTextY = qrY + 10;
  const footerHeight = footerLines.length * footerLineHeight;
  const signatureLineY = footerTextY + footerHeight + 5;
  const parentCopyY = signatureLineY + 6;

  doc.setDrawColor(190);
  doc.setLineWidth(0.2);
  doc.line(10, qrY + 24, right, qrY + 24);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(footerFontSize);
  doc.text(footerLines, pageWidth / 2, footerTextY, {
    align: "center",
    maxWidth: footerMaxWidth,
  });

  doc.setDrawColor(90);
  doc.setLineWidth(0.25);
  doc.line(150, signatureLineY, right, signatureLineY);
  doc.setFontSize(7.1);
  doc.text("Authorized Signature", right, signatureLineY - 1.8, {
    align: "right",
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.2);
  doc.text(data.parentCopyLabel, pageWidth / 2, parentCopyY, {
    align: "center",
  });

  const watermarkWidth = 70;
  const watermarkHeight = 70;
  const watermarkX = pageWidth / 2 - watermarkWidth / 2;
  const watermarkY = pageHeight / 2 - watermarkHeight / 2;
  const totalPages = doc.getNumberOfPages();
  for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
    doc.setPage(pageNumber);
    doc.addImage(
      watermarkLogoDataUrl,
      "PNG",
      watermarkX,
      watermarkY,
      watermarkWidth,
      watermarkHeight,
    );
  }
  doc.setPage(1);

  return doc.output("blob");
}

export async function printReceiptPdf(receipt = {}) {
  const blob = await buildReceiptPdf(receipt);
  if (typeof window === "undefined" || typeof document === "undefined") {
    return blob;
  }

  const url = URL.createObjectURL(blob);
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.left = "0";
  frame.style.top = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.src = url;

  const cleanup = () => {
    URL.revokeObjectURL(url);
    frame.remove();
  };

  frame.onload = () => {
    const frameWindow = frame.contentWindow;
    if (frameWindow) {
      frameWindow.focus();
      const safeCleanup = () => {
        frameWindow.removeEventListener("afterprint", safeCleanup);
        cleanup();
      };
      frameWindow.addEventListener("afterprint", safeCleanup, { once: true });
      frameWindow.print();
      // Fallback cleanup in case afterprint is not fired by browser.
      setTimeout(safeCleanup, 60000);
      return;
    }
    cleanup();
  };

  document.body.appendChild(frame);
  return blob;
}
